import {captureException, ErrorBoundary} from '@sentry/react';
import {useInitFlagsmith} from 'feature-flags';
import {showNotification} from 'platform/components';
import {Box, Show} from 'platform/foundation';

import {lazy, Suspense, useEffect} from 'react';
import {Outlet} from 'react-router-dom';

import {useGetCurrentUserInfoQuery} from '@dms/api/customer';
import {useListPermissionExpressionQuery} from '@dms/api/security';
import {browserStorageKey} from '@dms/config';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {getWorkspaceFromUri} from '@dms/routes';
import {CommonSkeleton, PromoFeaturePopup, useTenant} from '@dms/shared';

import {isRunningInCypress} from 'shared/web';

import {AiAssistantErrorSync} from '../AiAssistantErrorSync/AiAssistantErrorSync';
import {AiAssistantLanguageSync} from '../AiAssistantLanguageSync/AiAssistantLanguageSync';
import {AiAssistantLocationSync} from '../AiAssistantLocationSync/AiAssistantLocationSync';
import {PromptBridgeProvider} from '../PromptBridgeProvider/PromptBridgeProvider';
import {ApplicationUpdateBoundary} from './components/ApplicationUpdateBoundary';
import {GtmPageView} from './components/GtmPageView';
import {PrefetchBoundary} from './components/PrefetchBoundary';
import {SentryErrorBoundary} from './components/SentryErrorBoundary';
import {useFeatureFlagMockListener} from './hooks/useFeatureFlagMockListener';
import {usePromoFeature} from './hooks/usePromoFeature';
import {sentryInit} from './utils/sentryInit';

const Datadog = lazy(() => import('./components/Datadog'));
const Beamer = lazy(() => import('./components/Beamer'));
const Clarity = lazy(() =>
  import('./components/Clarity').then((module) => ({
    default: module.Clarity,
  }))
);
const OmneticAiAssistant = lazy(() =>
  import('@dms/features/ai-assistant-ui').then((module) => ({
    default: module.OmneticAiAssistant,
  }))
);
const AiAssistantCrashSync = lazy(() =>
  import('../AiAssistantCrashSync/AiAssistantCrashSync').then((module) => ({
    default: module.AiAssistantCrashSync,
  }))
);
const GlobalSearchProvider = lazy(() =>
  import('@dms/features/global-search').then((module) => ({
    default: module.GlobalSearchProvider,
  }))
);

export function Initialize() {
  const {workspace} = getWorkspaceFromUri();
  const refreshKey = useFeatureFlagMockListener();

  const {data: userInfo} = useGetCurrentUserInfoQuery();
  const {data: userPermissions} = useListPermissionExpressionQuery();

  const {isOpen, moreInfoRedirectUrl, handleClose} = usePromoFeature();
  const {isLoading: isTenantLoading} = useTenant();

  const language = userInfo?.settings?.language;

  const {isFlagSmithInitialized, areFeatureFlagsLoadedFromApi} = useInitFlagsmith({
    userEmail: userInfo?.email,
    accessTokenStorageKey: browserStorageKey.ACCESS_TOKEN,
    environmentId: process.env.FLAGSMITH_ENV!,
    flagsmithUrl: `${process.env.FLAGSMITH_URL}/api/v1/`,
  });

  useEffect(
    function initializeSessionTrackingAndLanguage() {
      const dataLayer = {
        environment: process.env.ENV_TYPE,
        workspace: workspace ?? '',
        userId: '',
      };
      if (userInfo && userInfo.id && userInfo.email && !isRunningInCypress()) {
        dataLayer.userId = userInfo.id;

        sentryInit(userInfo);

        if (!window.dataLayer) {
          captureException(
            'Google Tag Manager (GTM) is not initialized: window.dataLayer is not defined'
          );
          return;
        }
        window.dataLayer.push(dataLayer);
      }

      if (!language || language === localStorage[browserStorageKey.LAST_KNOWN_LANGUAGE]) {
        return;
      }
      i18n
        .changeLanguage(language)
        .then(() => localStorage.setItem(browserStorageKey.LAST_KNOWN_LANGUAGE, language))
        .catch(() =>
          showNotification.error('fatal error - failed to set the application language')
        );
    },
    [workspace, userInfo, language]
  );

  if (!userInfo || !language || !userPermissions || !isFlagSmithInitialized || isTenantLoading) {
    return <CommonSkeleton />;
  }

  return (
    <SentryErrorBoundary>
      <ErrorBoundary>
        <Show when={areFeatureFlagsLoadedFromApi}>
          <Suspense fallback={null}>
            <Datadog />
          </Suspense>
        </Show>
      </ErrorBoundary>
      <PrefetchBoundary>
        <ApplicationUpdateBoundary>
          <GtmPageView />
          <AiAssistantLocationSync />
          <AiAssistantLanguageSync language={language} />
          <AiAssistantErrorSync />
          <PromptBridgeProvider>
            <Box key={refreshKey} width="100%" height="100%">
              <Outlet />
            </Box>
          </PromptBridgeProvider>
          <Show whenFeatureEnabled={featureFlags.CORE_NEWS_POPUP}>
            <PromoFeaturePopup
              isOpen={isOpen}
              onClose={handleClose}
              data-testid="promo-feature-popup"
              moreInfoUrl={moreInfoRedirectUrl}
            />
          </Show>
          <Show whenFeatureEnabled={featureFlags.SCRIPT_BEAMER}>
            <Suspense fallback={null}>
              <Beamer />
            </Suspense>
          </Show>
          <Show whenFeatureEnabled={featureFlags.SCRIPT_CLARITY}>
            <ErrorBoundary>
              <Suspense fallback={null}>
                <Clarity />
              </Suspense>
            </ErrorBoundary>
          </Show>
          <Show whenFeatureEnabled={featureFlags.OMNETIC_AI_ASSISTANT}>
            <Suspense fallback={null}>
              <AiAssistantCrashSync />
              <OmneticAiAssistant />
            </Suspense>
          </Show>
          <Show whenFeatureEnabled={featureFlags.GLOBAL_SEARCH}>
            <ErrorBoundary>
              <Suspense fallback={null}>
                <GlobalSearchProvider />
              </Suspense>
            </ErrorBoundary>
          </Show>
        </ApplicationUpdateBoundary>
      </PrefetchBoundary>
    </SentryErrorBoundary>
  );
}
